import apiClient, { handleApiResponse, handleApiError } from './apiClient';
import authService from './authService';
import { hasActionAccess } from '../utils/roleBasedAccess';

/**
 * Candidate Service
 * Handles all candidate-related API calls with role-based access control
 */
class CandidateService {
  constructor() {
    this.baseURL = '/candidate';
  }

  // Authentication services
  async registerCandidate(candidateData) {
    try {
      const response = await apiClient.post('/auth/candidate/register', candidateData);
      return response.data;
    } catch (error) {
      console.error('Candidate registration failed:', error);
      throw error;
    }
  }

  // Profile management
  async getProfile() {
    try {
      const response = await apiClient.get(`${this.baseURL}/profile`);
      return response.data;
    } catch (error) {
      console.error('Failed to get candidate profile:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await apiClient.put(`${this.baseURL}/profile`, profileData);
      return response.data;
    } catch (error) {
      console.error('Failed to update candidate profile:', error);
      throw error;
    }
  }

  // Application management
  async getApplications() {
    try {
      // Use the authenticated candidate endpoint to get applications
      const response = await apiClient.get(`${this.baseURL}/applications`);
      // Ensure we return a consistent structure
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          // If we get an array directly, wrap it in an object
          return { applications: response.data };
        } else if (response.data.applications) {
          // If we get the expected structure, return as is
          return response.data;
        } else {
          // If we get an object without applications array, return it with empty array
          return { applications: [], ...response.data };
        }
      } else {
        // If we get unexpected data, return empty structure
        return { applications: [] };
      }
    } catch (error) {
      console.error('Failed to get applications:', error);
      // Return empty structure on error to prevent frontend crashes
      return { applications: [] };
    }
  }

  async getApplicationById(applicationId) {
    try {
      const response = await apiClient.get(`${this.baseURL}/applications/${applicationId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get application:', error);
      throw error;
    }
  }

  // Dashboard statistics
  async getDashboardStats() {
    try {
      const response = await apiClient.get(`${this.baseURL}/dashboard/stats`);
      // Ensure we return a consistent structure
      if (response.data && typeof response.data === 'object') {
        return response.data;
      } else {
        // If we get unexpected data, return empty structure
        return {
          total_applications: 0,
          pending_applications: 0,
          interviews_scheduled: 0,
          profile_completion: 0,
          recent_activity_count: 0
        };
      }
    } catch (error) {
      console.error('Failed to get dashboard stats:', error);
      // Return default structure on error
      return {
        total_applications: 0,
        pending_applications: 0,
        interviews_scheduled: 0,
        profile_completion: 0,
        recent_activity_count: 0
      };
    }
  }

  // HR/Admin candidate management
  async getCandidates(params = {}) {
    try {
      const response = await apiClient.get('/candidates', { params });
      
      // Log the response for debugging
      console.log('getCandidates response:', response.data);
      
      // Return the candidates array from the response structure
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data?.candidates && Array.isArray(response.data.candidates)) {
        return response.data.candidates;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else {
        console.warn('Unexpected response structure, returning empty array:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Failed to get candidates:', error);
      // Return empty array on error to prevent frontend crashes
      return [];
    }
  }

  async getCandidateById(candidateId) {
    try {
      const response = await apiClient.get(`/candidates/${candidateId}`);
      return response.data?.candidate || response.data;
    } catch (error) {
      console.error('Failed to get candidate:', error);
      throw error;
    }
  }

  // Job-related services (using public endpoints)
  async getPublicJobs(params = {}) {
    try {
      const response = await apiClient.get('/public/jobs', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to get public jobs:', error);
      throw error;
    }
  }

  async getPublicJob(jobId) {
    try {
      const response = await apiClient.get(`/public/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get public job:', error);
      throw error;
    }
  }

  async submitJobApplication(applicationData) {
    try {
      const response = await apiClient.post('/public/job-applications/submit', applicationData);
      return response.data;
    } catch (error) {
      console.error('Failed to submit job application:', error);
      throw error;
    }
  }

  // Public application tracking by email
  async trackApplicationsByEmail(email) {
    try {
      const response = await apiClient.get(`/public/applications/track?email=${encodeURIComponent(email)}`);
      // Ensure we return a consistent structure
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          // If we get an array directly, wrap it in an object
          return { applications: response.data };
        } else if (response.data.applications) {
          // If we get the expected structure, return as is
          return response.data;
        } else {
          // If we get an object without applications array, return it with empty array
          return { applications: [], ...response.data };
        }
      } else {
        // If we get unexpected data, return empty structure
        return { applications: [] };
      }
    } catch (error) {
      console.error('Failed to track applications:', error);
      // Return empty structure on error to prevent frontend crashes
      return { applications: [] };
    }
  }

  // Interview management (candidate view)
  async getMyInterviews() {
    try {
      const response = await apiClient.get(`${this.baseURL}/interviews`);
      return response.data;
    } catch (error) {
      console.error('Failed to get interviews:', error);
      // Return empty array if endpoint doesn't exist yet
      return { interviews: [] };
    }
  }

  // Profile completion helper
  calculateProfileCompletion(profile) {
    if (!profile) return 0;

    const fields = [
      'skills',
      'experience_summary',
      'preferred_location',
      'salary_expectation',
      'availability_date',
      'linkedin_url',
      'education',
      'work_experience'
    ];

    const completedFields = fields.filter(field => {
      const value = profile[field];
      if (Array.isArray(value)) {
        return value && value.length > 0;
      }
      return value && value.toString().trim() !== '';
    });

    return Math.round((completedFields.length / fields.length) * 100);
  }

  // Utility methods
  getApplicationStatusColor(status) {
    const statusColors = {
      'uploaded': 'blue',
      'processing': 'yellow',
      'completed': 'green',
      'in_review': 'orange',
      'accepted': 'emerald',
      'rejected': 'red',
      'pending': 'gray'
    };
    return statusColors[status] || 'gray';
  }

  getApplicationStatusText(status) {
    const statusTexts = {
      'uploaded': 'Application Submitted',
      'processing': 'Under Review',
      'completed': 'Review Complete',
      'in_review': 'In Review',
      'accepted': 'Accepted',
      'rejected': 'Not Selected',
      'pending': 'Pending'
    };
    return statusTexts[status] || status;
  }

  formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  }

  formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return 'N/A';
    }
  }
}

const candidateService = new CandidateService();
export default candidateService;

// Named exports for convenience
export {
  candidateService
};